import React, { useEffect, useRef, useState, useCallback } from 'react';
import { GuidedSession } from '../data/guidedSessions';
import { PhaseKind } from '../data/protocols';
import { ProtocolAudio, AudioExtras } from '../services/protocolAudio';
import { frameAtElapsed, sessionTotalSec } from '../services/guidedSequencer';
import { ticksToFire } from '../services/runFormat';
import { useLanguage } from '../i18n/LanguageContext';
import RunnerControls from './RunnerControls';
import RunSurface from './RunSurface';
import BreathOrb from './BreathOrb';
import { AURORA, PHASE_ORB } from '../data/runTheme';

interface GuidedSessionRunnerProps {
  session: GuidedSession;
  soundEnabled: boolean;
  cuesEnabled: boolean;
  volume: number;
  extras: AudioExtras;
  audioCtx?: AudioContext | null;
  onComplete: () => void;
  onQuit: () => void;
  onPauseChange?: (paused: boolean) => void;
}

const BREATH_HALF = 5; // 5-5 coherence breath, seconds per half
const easeInOutSine = (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2;

const GuidedSessionRunner: React.FC<GuidedSessionRunnerProps> = ({
  session,
  soundEnabled,
  cuesEnabled,
  volume,
  extras,
  audioCtx,
  onComplete,
  onQuit,
  onPauseChange,
}) => {
  const { language, t } = useLanguage();
  const pick = (b: { no: string; en: string }) => (language === 'en' ? b.en : b.no);

  const [phaseIndex, setPhaseIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [breathKind, setBreathKind] = useState<PhaseKind>('inhale');
  const [breathProgress, setBreathProgress] = useState(0);
  const [paused, setPaused] = useState(false);

  const audioRef = useRef<ProtocolAudio | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef(0);
  const elapsedRef = useRef(0);
  const phaseIndexRef = useRef(0);
  const breathKindRef = useRef<PhaseKind>('inhale');
  const doneRef = useRef(false);
  const extrasRef = useRef(extras); extrasRef.current = extras;
  const halfPrevRef = useRef(0);
  const pausedRef = useRef(false);

  const total = sessionTotalSec(session);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    audioRef.current?.bell();
    audioRef.current?.stop();
    audioRef.current = null;
    onComplete();
  }, [onComplete]);

  // User-initiated exit: tear down and return to the menu (no completion bell,
  // no advancing to the next segment).
  const quit = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    audioRef.current?.stop();
    audioRef.current = null;
    onQuit();
  }, [onQuit]);

  // Apply the audio bed appropriate to the phase we just entered.
  const enterPhase = useCallback(
    (index: number) => {
      const phase = session.phases[index];
      audioRef.current?.bell();
      if (phase.breath) {
        breathKindRef.current = 'inhale';
        setBreathKind('inhale');
        if (cuesEnabled) audioRef.current?.cue('inhale');
      } else {
        audioRef.current?.ambient(session.minimalSound ? 0 : 0.12);
      }
    },
    [session, cuesEnabled]
  );

  const tick = useCallback(
    (ts: number) => {
      const delta = lastTsRef.current ? (ts - lastTsRef.current) / 1000 : 0;
      lastTsRef.current = ts;

      elapsedRef.current += delta;
      setElapsed(elapsedRef.current);

      const frame = frameAtElapsed(session, elapsedRef.current);
      if (frame.done) {
        finish();
        return;
      }

      if (frame.index !== phaseIndexRef.current) {
        phaseIndexRef.current = frame.index;
        setPhaseIndex(frame.index);
        enterPhase(frame.index);
      }

      const phase = session.phases[frame.index];
      if (phase.breath) {
        const pos = frame.phaseElapsedSec % (BREATH_HALF * 2);
        const isInhale = pos < BREATH_HALF;
        const kind: PhaseKind = isInhale ? 'inhale' : 'exhale';
        const progress = (isInhale ? pos : pos - BREATH_HALF) / BREATH_HALF;
        audioRef.current?.setPhase(kind, progress);
        if (kind !== breathKindRef.current) {
          breathKindRef.current = kind;
          setBreathKind(kind);
          // Cue the new breath action word exactly at the switch.
          if (cuesEnabled) audioRef.current?.cue(kind);
        }
        setBreathProgress(progress);

        const half = pos < BREATH_HALF ? pos : pos - BREATH_HALF; // 0..5 within this inhale/exhale

        const counting = extrasRef.current.counting;
        if (counting && counting !== 'off') {
          const marks = ticksToFire(halfPrevRef.current, half, BREATH_HALF);
          for (const s of marks) {
            audioRef.current?.tick();
            if (counting === 'countWarn' && s === BREATH_HALF - 1) audioRef.current?.changeTone();
          }
          halfPrevRef.current = half;
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, cuesEnabled, finish, enterPhase]
  );

  const handlePause = useCallback(() => {
    if (doneRef.current || pausedRef.current) return;
    pausedRef.current = true;
    setPaused(true);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    audioRef.current?.pause();
    onPauseChange?.(true);
  }, [onPauseChange]);

  const handleResume = useCallback(() => {
    if (!pausedRef.current) return;
    pausedRef.current = false;
    setPaused(false);
    lastTsRef.current = 0;
    audioRef.current?.resume();
    onPauseChange?.(false);
    rafRef.current = requestAnimationFrame(tick);
  }, [tick, onPauseChange]);

  useEffect(() => {
    const audio = new ProtocolAudio(soundEnabled, volume, cuesEnabled, extras, audioCtx ?? null);
    audio.start(session.sound);
    audioRef.current = audio;
    enterPhase(0);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      audioRef.current?.stop();
      audioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phase = session.phases[phaseIndex];
  const frame = frameAtElapsed(session, elapsed);
  const phaseRemaining = Math.max(0, Math.ceil(frame.phaseRemainingSec));
  const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const overallPct = Math.min(100, (elapsed / total) * 100);

  // Breathing circle (only meaningful during a breath phase)
  const breathSize = breathKind === 'inhale' ? easeInOutSine(breathProgress) : 1 - easeInOutSine(breathProgress);
  const scale = 0.4 + breathSize * 0.6;
  const breathCount = Math.max(1, Math.ceil(BREATH_HALF * (1 - breathProgress)));

  return (
    <RunSurface>
      {phase.breath ? (
        <BreathOrb
          scale={scale}
          color={PHASE_ORB[breathKind]}
          progress={breathProgress}
          label={breathKind === 'inhale' ? t('breathing.inhale') : t('breathing.exhale')}
          count={breathCount}
          showRing
        />
      ) : (
        <div style={{ width: 260, height: 260, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', width: 240, height: 240, borderRadius: '50%', border: '1px solid #ffffff14' }} />
          <span style={{ fontSize: '3.75rem', fontWeight: 300, color: AURORA.textPrimary, fontVariantNumeric: 'tabular-nums', textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>
            {mmss(phaseRemaining)}
          </span>
        </div>
      )}

      <h2 style={{ fontSize: '1.5rem', fontWeight: 500, color: AURORA.textPrimary, margin: '1.25rem 0 0.25rem' }}>{pick(phase.title)}</h2>
      <p style={{ textAlign: 'center', maxWidth: '28rem', minHeight: '3rem', padding: '0 1rem', color: AURORA.textSecondary }}>{pick(phase.cue)}</p>

      {phase.breath && (
        <p style={{ fontSize: '0.875rem', color: AURORA.textMuted, marginBottom: '0.5rem', fontVariantNumeric: 'tabular-nums' }}>
          {t('protocols.phaseLeft')}: {mmss(phaseRemaining)}
        </p>
      )}

      <div style={{ width: '100%', maxWidth: '20rem', height: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 999, overflow: 'hidden', margin: '0.5rem 0 1.5rem' }}>
        <div style={{ height: '100%', width: `${overallPct}%`, background: AURORA.accent, transition: 'width 0.3s ease' }} />
      </div>

      <RunnerControls
        paused={paused}
        onPause={handlePause}
        onResume={handleResume}
        onStop={quit}
      />
    </RunSurface>
  );
};

export default GuidedSessionRunner;
